/**
 * Content View Detail Page
 * 
 * This page displays a single view accessed from the content search page.
 * It maintains the proper navigation history so browser back button returns
 * to the view rather than skipping back to the content search.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { useOrganization } from '../context/OrganizationContext';
import { View } from '../types/view.types';
import { viewsApiService } from '../services/views.api';
import ViewRenderer from '../components/content/ViewRenderer';

const ContentViewDetail: React.FC = () => {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const { teams, currentTeamId } = useOrganization();
  
  const [view, setView] = useState<View | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get team context
  const currentTeam = teams.find(team => team.id === currentTeamId);
  const teamId = currentTeam?.id;
  const organizationId = currentTeam?.organizationId;
  
  useEffect(() => {
    if (viewId) {
      loadView();
    }
  }, [viewId]);
  
  const loadView = async () => {
    if (!viewId || !currentTeamId) return;

    setLoading(true);
    setError(null);

    try {
      const loadedView = await viewsApiService.getView(viewId, currentTeamId);
      setView(loadedView);
    } catch (err) {
      console.error('Error loading view:', err);
      setError('Failed to load view. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    // Navigate back to content page
    navigate('/content');
  };
  
  if (!organizationId || !teamId) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="warning">
          Please select a team to view content.
        </Alert>
      </Container>
    );
  }
  
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error || !view) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">
          {error || 'View not found'}
        </Alert>
      </Container>
    );
  }
  
  // Render the view using ViewRenderer component
  return (
    <ViewRenderer
      view={view}
      organizationId={organizationId}
      teamId={teamId}
      onBack={handleBack}
      showHeader={true}
    />
  );
};

export default ContentViewDetail;